import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Campaign } from '../../types/campaign';
import type { MessageTemplate } from '../../types/messageTemplate';
import type { WhatsAppSession } from '../../types/api';
import type { Assistant } from '../../types/assistant';
import type { LeadGroup } from '../../types/leadGroup';
import type { Representative } from '../../types/representative';
import {
  messageTemplateService,
  sessionService,
  assistantService,
  leadGroupService,
  representativeService
} from '../../services';
import messageQueueService from '../../services/messageQueue.service';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormTextarea, Button, Tabs } from '../ui';

interface CampaignFormProps {
  campaign: Campaign | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function CampaignForm({ campaign, onSubmit, isLoading, onClose }: CampaignFormProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    daily_limit: '',
    status: 'active',
    template_id: '',
    whatsapp_id: '',
    assistant_id: '',
    queue_id: '',
    time_to_send: '',
    filter_linked_representative: '',
    filter_representative_id: '',
    filter_linked_lead_group: '',
    filter_lead_group_id: '',
    filter_last_contact_days: '',
    filter_origin_lead: '',
    filter_type: '',
    filter_no_services: '',
    filter_with_service: '',
  });

  const { data: userTemplates } = useQuery({
    queryKey: ['user-templates'],
    queryFn: () => messageTemplateService.getUserTemplates(),
  });

  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const { data: leadGroups } = useQuery({
    queryKey: ['lead-groups'],
    queryFn: () => leadGroupService.getAll(),
  });

  const { data: representativesData } = useQuery({
    queryKey: ['representatives'],
    queryFn: () => representativeService.getAll(1, 1000),
  });

  const representatives = representativesData?.data || [];

  const { data: queues } = useQuery({
    queryKey: ['message-queues'],
    queryFn: () => messageQueueService.getQueues(),
  });

  useEffect(() => {
    if (campaign) {
      const linkedRep = campaign.filter_linked_representative === null ? '' :
                       campaign.filter_linked_representative ? 'true' : 'false';
      const linkedGroup = campaign.filter_linked_lead_group === null ? '' :
                         campaign.filter_linked_lead_group ? 'true' : 'false';

      setFormData({
        name: campaign.name,
        description: campaign.description || '',
        start_date: campaign.start_date.split('T')[0],
        end_date: campaign.end_date.split('T')[0],
        daily_limit: campaign.daily_limit?.toString() || '',
        status: campaign.status,
        template_id: campaign.template_id.toString(),
        whatsapp_id: campaign.whatsapp_id?.toString() || '',
        assistant_id: campaign.assistant_id?.toString() || '',
        queue_id: campaign.queue_id?.toString() || '',
        time_to_send: campaign.time_to_send || '',
        filter_linked_representative: linkedRep,
        filter_representative_id: campaign.filter_representative_id?.toString() || '',
        filter_linked_lead_group: linkedGroup,
        filter_lead_group_id: campaign.filter_lead_group_id?.toString() || '',
        filter_last_contact_days: campaign.filter_last_contact_days?.toString() || '',
        filter_origin_lead: campaign.filter_origin_lead || '',
        filter_type: campaign.filter_type || '',
        filter_no_services: campaign.filter_no_services?.join(', ') || '',
        filter_with_service: campaign.filter_with_service?.join(', ') || '',
      });
    }
  }, [campaign]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Form onSubmit={onSubmit}>
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Informações Gerais
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('filters')}
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'filters'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Filtros de Disparo
          </button>
        </div>
      </div>

      <div className={activeTab === 'general' ? 'block' : 'hidden'}>
        <div className="space-y-4">
          <FormField>
            <FormLabel htmlFor="name" required>
              Nome
            </FormLabel>
            <FormInput
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              maxLength={150}
              placeholder="Nome da campanha"
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="description">Descrição</FormLabel>
            <FormTextarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Descrição da campanha"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="start_date" required>
                Data de Início
              </FormLabel>
              <FormInput
                id="start_date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="end_date" required>
                Data de Término
              </FormLabel>
              <FormInput
                id="end_date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="template_id" required>
              Template
            </FormLabel>
            <FormSelect
              id="template_id"
              name="template_id"
              value={formData.template_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecione um template</option>
              {userTemplates?.map((template: MessageTemplate) => (
                <option key={template.id} value={template.id}>
                  {template.name || template.category_description || `Template ${template.id}`}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="whatsapp_id">Conexão</FormLabel>
            <FormSelect
              id="whatsapp_id"
              name="whatsapp_id"
              value={formData.whatsapp_id}
              onChange={handleChange}
            >
              <option value="">Selecione uma conexão (opcional)</option>
              {sessions?.map((session: WhatsAppSession) => (
                <option key={session.id} value={session.id}>
                  {session.session_name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="queue_id" required>Fila</FormLabel>
            <FormSelect
              id="queue_id"
              name="queue_id"
              value={formData.queue_id}
              onChange={handleChange}
              required
            >
              <option value="">Selecione uma fila</option>
              {queues?.map((queue) => (
                <option key={queue.id} value={queue.id}>
                  {queue.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="assistant_id">Assistente</FormLabel>
            <FormSelect
              id="assistant_id"
              name="assistant_id"
              value={formData.assistant_id}
              onChange={handleChange}
            >
              <option value="">Selecione um assistente (opcional)</option>
              {assistants?.map((assistant: Assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField>
              <FormLabel htmlFor="daily_limit">Limite Diário de Mensagens</FormLabel>
              <FormInput
                id="daily_limit"
                name="daily_limit"
                type="number"
                min="0"
                value={formData.daily_limit}
                onChange={handleChange}
                placeholder="Deixe vazio para ilimitado"
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="time_to_send">Horário de Envio</FormLabel>
              <FormInput
                id="time_to_send"
                name="time_to_send"
                type="time"
                value={formData.time_to_send}
                onChange={handleChange}
                placeholder="Ex: 09:00"
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="status" required>
                Status
              </FormLabel>
              <FormSelect
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="active">Ativa</option>
                <option value="paused">Pausada</option>
                <option value="completed">Concluída</option>
                <option value="cancelled">Cancelada</option>
              </FormSelect>
            </FormField>
          </div>
        </div>
      </div>

      <div className={activeTab === 'filters' ? 'block' : 'hidden'}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="filter_linked_representative">
                Filtro de Vendedor
              </FormLabel>
              <FormSelect
                id="filter_linked_representative"
                name="filter_linked_representative"
                value={formData.filter_linked_representative}
                onChange={handleChange}
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel htmlFor="filter_representative_id">
                Vendedor
              </FormLabel>
              <FormSelect
                id="filter_representative_id"
                name="filter_representative_id"
                value={formData.filter_representative_id}
                onChange={handleChange}
                disabled={formData.filter_linked_representative !== 'true'}
              >
                <option value="">Selecione um vendedor</option>
                {representatives.map((rep: Representative) => (
                  <option key={rep.id} value={rep.id}>
                    {rep.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="filter_linked_lead_group">
                Filtro de Grupo do Lead
              </FormLabel>
              <FormSelect
                id="filter_linked_lead_group"
                name="filter_linked_lead_group"
                value={formData.filter_linked_lead_group}
                onChange={handleChange}
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel htmlFor="filter_lead_group_id">
                Grupo do Lead
              </FormLabel>
              <FormSelect
                id="filter_lead_group_id"
                name="filter_lead_group_id"
                value={formData.filter_lead_group_id}
                onChange={handleChange}
                disabled={formData.filter_linked_lead_group !== 'true'}
              >
                <option value="">Selecione um grupo</option>
                {leadGroups?.map((group: LeadGroup) => (
                  <option key={group.id} value={group.id}>
                    {group.description}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="filter_last_contact_days">
                Últimos Dias de Contato
              </FormLabel>
              <FormInput
                id="filter_last_contact_days"
                name="filter_last_contact_days"
                type="number"
                min="0"
                value={formData.filter_last_contact_days}
                onChange={handleChange}
                placeholder="Ex: 30 (últimos 30 dias)"
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="filter_origin_lead">
                Origem do Lead
              </FormLabel>
              <FormInput
                id="filter_origin_lead"
                name="filter_origin_lead"
                value={formData.filter_origin_lead}
                onChange={handleChange}
                maxLength={100}
                placeholder="Ex: Facebook, Google Ads"
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="filter_type">Tipo de Lead</FormLabel>
            <FormSelect
              id="filter_type"
              name="filter_type"
              value={formData.filter_type}
              onChange={handleChange}
            >
              <option value="">Todos os tipos</option>
              <option value="administrative">Administrativo</option>
              <option value="registration">Cadastro</option>
              <option value="commercial">Comercial</option>
              <option value="financial">Financeiro</option>
              <option value="legal_representative">Representante Legal</option>
              <option value="technical">Técnico</option>
              <option value="other">Outro</option>
            </FormSelect>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="filter_with_service">
                Leads Com Serviços (IDs)
              </FormLabel>
              <FormInput
                id="filter_with_service"
                name="filter_with_service"
                value={formData.filter_with_service}
                onChange={handleChange}
                placeholder="Ex: 1, 2, 3"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                IDs dos serviços separados por vírgula
              </p>
            </FormField>

            <FormField>
              <FormLabel htmlFor="filter_no_services">
                Leads Sem Serviços (IDs)
              </FormLabel>
              <FormInput
                id="filter_no_services"
                name="filter_no_services"
                value={formData.filter_no_services}
                onChange={handleChange}
                placeholder="Ex: 1, 2, 3"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                IDs dos serviços separados por vírgula
              </p>
            </FormField>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end space-x-3">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {campaign ? 'Atualizar' : 'Criar'} Campanha
        </Button>
      </div>
    </Form>
  );
}

export default CampaignForm;
